import { PaymentIntentCreateBlockedError } from '@ravago/shared/next-data-access';
import { PaymentUtils } from '@ravago/shared/next-data-access/client';
import { PaymentIntent } from '@ravago/shared/page-data/models/features';
import { useTranslations } from '@ravago/shared/translations/client';

import { useIsMutating, useMutation, useMutationState, useQuery, useQueryClient } from '@tanstack/react-query';

import { parseAsInteger, useQueryState } from 'nuqs';

export interface UsePaymentIntentResult {
   paymentIntent: PaymentIntent | undefined;
   isError: boolean;
   isPending: boolean;
   errorMessage: string | null;
   create: (invoiceIds: string[]) => Promise<void>;
   recreate: () => Promise<void>;
   clear: () => Promise<void>;
}

const mutationKey = ['payment-intent'];

export const usePaymentIntent = ({ enabled = true }: { enabled?: boolean } = {}): UsePaymentIntentResult => {
   const translate = useTranslations();
   const queryClient = useQueryClient();

   const [paymentIntentId, setPaymentIntentId] = useQueryState('rav_payment_intent', parseAsInteger);

   const {
      isError: isMutateError,
      mutate,
      reset
   } = useMutation({
      mutationKey,
      mutationFn: async (invoiceIds: string[]) => PaymentUtils.createPaymentIntent(invoiceIds),
      onSuccess: async (data) => {
         queryClient.setQueryData(['payment-intents', data.id], data);
         await setPaymentIntentId(data.id);
      }
   });

   const mutationErrors = useMutationState({ filters: { mutationKey }, select: (mutation) => mutation.state.error });

   const {
      data,
      isLoading,
      isError: isFetchError,
      error: fetchError
   } = useQuery({
      queryKey: ['payment-intents', paymentIntentId],
      queryFn: async () => PaymentUtils.getPaymentIntent(paymentIntentId!),
      enabled: enabled && !!paymentIntentId,
      staleTime: Infinity
   });

   const isMutatingPaymentIntent = useIsMutating({ mutationKey });

   const mapErrorToMessage = (error: Error) => {
      if (error instanceof PaymentIntentCreateBlockedError) {
         return translate('invoices.payment.payment-blocked-error');
      }

      return translate('invoices.payment.start-payment-error');
   };

   const getErrorMessage = () => {
      const lastMutationError = mutationErrors.at(-1);
      if (lastMutationError) return mapErrorToMessage(lastMutationError);
      if (fetchError) return mapErrorToMessage(fetchError);
      return null;
   };

   const create = async (invoiceIds: string[]) => {
      mutate(invoiceIds);
   };

   const recreate = async () => {
      let localData: PaymentIntent | undefined;
      try {
         localData = await queryClient.ensureQueryData<PaymentIntent>({ queryKey: ['payment-intents', paymentIntentId] });
      } catch (_) {
         // No need to handle error, it will be handled by useQuery
      }
      if (!localData) return;

      const isCancelled = localData.statuses.some(({ status }) => status === 'canceled');
      if (!isCancelled) return;

      const invoiceIds = localData.transactions.map((transaction) => transaction.id);
      if (invoiceIds.length > 0) {
         reset();
         mutate(invoiceIds);
      }
      await setPaymentIntentId(null);
   };

   const clear = async () => {
      reset();
      queryClient.removeQueries({ queryKey: ['payment-intents', paymentIntentId] });
      await setPaymentIntentId(null);
   };

   return {
      paymentIntent: data,
      isPending: isMutatingPaymentIntent > 0 || isLoading,
      isError: isMutateError || isFetchError,
      errorMessage: getErrorMessage(),
      create,
      recreate,
      clear
   };
};
